// Money arithmetic in this app runs on JavaScript floats, so a subtraction of two priced amounts
// lands a fraction of a cent away from the true answer: `1200.3 - 1200.1` is
// `0.20000000000004547`. Left alone that reaches `formatCurrency` and a biller reads a balance that
// does not exist. Every derived money figure is rounded to a whole cent before it is displayed or
// written.
//
// PHI: amounts only. Nothing here reads a subject or a payer, and nothing is logged.

/**
 * A money amount rounded to a whole cent, as `buildInvoice` does when it sums prices.
 *
 * @param value A money amount, possibly carrying float drift from arithmetic.
 * @returns The same amount rounded to two decimal places.
 */
export function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Whether a money amount is exactly zero once float drift is rounded away.
 *
 * This checks strict equality, not `<= 0`. A negative balance is a credit or overpayment, not
 * "nothing owed". A caller that means "nothing left to bill" should check that directly instead of
 * reusing this helper.
 *
 * @param value A money amount, possibly carrying float drift from arithmetic.
 * @returns True when the rounded amount is exactly zero.
 */
export function isZeroBalance(value: number): boolean {
  return roundToCents(value) === 0;
}
